import { Injectable } from '@angular/core';
import { concat, Observable, of } from 'rxjs';
import { concatMap, map, take, tap, toArray } from 'rxjs/operators';

import {
  DEALER_OFFER_PRICING_MAP,
  MANUFACTURER_OFFER_PRICING_MAP
} from 'app/constants/pricing.data';
import {
  mergeLineElementsPricing,
  setPriceRateByStepId,
  setPricingObjectValue,
  setPricingObjectValueChanged,
  updateLineElementsPricingByKey
} from 'app/utils/pricing-utils';
import {
  AppCustomLine as CustomLine,
  AppProductLine as ProductLine,
  AppQuote as Quote,
  Offer,
  OfferProductPosition,
  PositionPricingUpdate
} from 'graphql/models/extended';
import { Condition, ConditionType, DocumentVersionInput } from 'graphql/models/generated';
import { EnsurePricesUpdatedMutation } from 'graphql/mutations/ensure-prices-updated';
import { EnsureOfferPricesUpdatedMutation } from 'graphql/mutations/ensure-offer-prices-updated';
import { RemoveOfferPricingMutation } from 'graphql/mutations/remove-offer-pricing';
import { RemovePositionPricingMutation } from 'graphql/mutations/remove-position-pricing';
import { RemovePricingCommandsMutation } from 'graphql/mutations/remove-pricing-commands';
import { SetAutoUpdatePricesMutation } from 'graphql/mutations/set-auto-update-prices';
import { SetOfferSurchargeOrDiscountMutation } from 'graphql/mutations/set-offer-surcharge-or-discount';
import { UpdateOfferPricingMutation } from 'graphql/mutations/update-offer-pricing';
import { UpdatePricingMutation } from 'graphql/mutations/update-pricing';
import { ConditionTypeENUM } from 'models/condition.model';
import { OfferBase } from 'models/offer.model';
import { CustomPricing, ElementPricing, OfferPricing, PriceDescriptor, ProductPricing } from 'models/pricing.model';
import { OfferMapperService } from 'shared/services/offer-mapper.service';
import { StoreService } from 'shared/services/store.service';
import { UpdateOfferCalculationTypeMutation } from 'graphql/mutations/update-offer-calculation-type';
import { mergeOfferPricingState } from 'app/offer/offer-utils';

@Injectable({
  providedIn: 'root'
})
export class PricingService {
  constructor(
    private storeService: StoreService,
    private offerMapperService: OfferMapperService,
    private updatePricingMutation: UpdatePricingMutation,
    private setOfferSurchargeOrDiscountMutation: SetOfferSurchargeOrDiscountMutation,
    private updateOfferCalculationTypeMutation: UpdateOfferCalculationTypeMutation,
    private updateOfferPricingMutation: UpdateOfferPricingMutation,
    private removeOfferPricingMutation: RemoveOfferPricingMutation,
    private removePositionPricingMutation: RemovePositionPricingMutation,
    private removePricingCommandsMutation: RemovePricingCommandsMutation,
    private setAutoUpdatePricesMutation: SetAutoUpdatePricesMutation,
    private ensurePricesUpdatedMutation: EnsurePricesUpdatedMutation,
    private ensureOfferPricesUpdatedMutation: EnsureOfferPricesUpdatedMutation
  ) {
  }

  /** Calculates the pricing update from the `priceCalcluation` on all `lines` of the `quote`. */
  calculatePricingUpdate(quote: Quote): PositionPricingUpdate | undefined {
    if (!quote || !quote.lines) {
      return undefined;
    }

    const pricingUpdate: PositionPricingUpdate = {};
    quote.lines.forEach((line) => {
      if ('pricing' in line && line.pricing) {
        pricingUpdate[line.id] = line.pricing;
      }
    });

    return pricingUpdate;
  }

  /** Extracts the already calculated `pricing` from all `productPositions` and nested `positions` of the `offer`. */
  extractPricingUpdate(offer: Offer): PositionPricingUpdate | undefined {
    if (!offer || !offer.lines || !offer.productPositions) {
      return undefined;
    }

    const pricingUpdate: PositionPricingUpdate = {};
    offer.productPositions.forEach((productPosition) => {
      productPosition.positions.forEach((position) => {
        if ('pricing' in position && position.pricing) {
          pricingUpdate[position.id] = position.pricing;
        }
        if ((position as ProductLine).subLines) {
          const subLines = (position as ProductLine)?.subLines;
          if (subLines) {
            subLines.forEach((subPos) => {
              const bomPos = subPos as ProductLine;
              if ('pricing' in bomPos && bomPos.pricing) {
                pricingUpdate[bomPos.id] = bomPos.pricing;
              }
            });
          }
        }
      });
    });

    return pricingUpdate;
  }

  applyPricingUpdate(
    productPositions: OfferProductPosition<ProductLine | CustomLine>[],
    pricingUpdate: PositionPricingUpdate
  ): OfferProductPosition[] {
    if (!productPositions || productPositions.length === 0) {
      return [];
    }

    return productPositions.map((productPosition) => {
      const positions = productPosition.positions.map((position) => {
        if (position.subLines?.length) {
          const subLines = position.subLines.map((subLine) => {
            if (Object.prototype.hasOwnProperty.call(pricingUpdate, subLine.id)) {
              return { ...subLine, pricing: { ...pricingUpdate[subLine.id] } as any };
            }

            return subLine;
          });
          // eslint-disable-next-line no-param-reassign
          position = { ...position, subLines };
        }

        if (Object.prototype.hasOwnProperty.call(pricingUpdate, position.id)) {
          return { ...position, pricing: { ...pricingUpdate[position.id] } as any };
        }

        return position;
      });

      return { ...productPosition, positions };
    });
  }

  setOfferPricing(
    offer: Pick<Offer, 'documentId' | 'revision'>,
    conditions: Condition,
    withPricing = true
  ): Observable<Quote> {
    const { type } = conditions;
    const documentVersion: DocumentVersionInput = {
      documentId: offer.documentId,
      revision: offer.revision
    };

    return this.setOfferCalculationType(documentVersion, type).pipe(
      concatMap(() => this.setOfferSurchargeOrDiscount(documentVersion, conditions, withPricing)),
      map((quote: Quote) => quote),
      take(1)
    );
  }

  setOfferSurchargeOrDiscount(
    documentVersion: DocumentVersionInput,
    conditions: Condition,
    withPricing = true
  ): Observable<Quote> {
    const { type, discount, surcharge } = conditions;
    const value = type === ConditionTypeENUM.SURCHARGE ? Number(surcharge) || 0 : Number(discount) || 0;
    const key = type === ConditionTypeENUM.SURCHARGE ? 'buyPriceSurcharge' : 'listPriceDiscount';

    return this.setOfferSurchargeOrDiscountMutation
      .mutate(
        {
          documentVersion,
          priceLineId: DEALER_OFFER_PRICING_MAP[key],
          stepId: DEALER_OFFER_PRICING_MAP[key],
          value,
          withPricing
        },
        { fetchPolicy: 'no-cache' }
      )
      .pipe<Quote, Quote>(
        map((r) => r.data?.quotes?.modify?.setPricingCommand as Quote),
        take(1)
      );
  }

  setOfferCalculationType(documentVersion: DocumentVersionInput, type: ConditionType) {
    return this.updateOfferCalculationTypeMutation.mutate(
      {
        documentVersion,
        type: type
      },
      { fetchPolicy: 'no-cache' }
    );
  }

  resetOfferManufacturerDiscount(offer: Offer) {
    return this.updateOfferPricing(offer, 0, MANUFACTURER_OFFER_PRICING_MAP.sellDiscount);
  }

  resetOfferManufacturerAlternativeDiscount(offer: Offer) {
    const priceDescriptor: PriceDescriptor = {
      lineId: DEALER_OFFER_PRICING_MAP.manufacturerAlternativeDiscount,
      stepId: DEALER_OFFER_PRICING_MAP.manufacturerAlternativeDiscount,
      value: 0
    };

    return this.removeOfferPricing(offer, priceDescriptor);
  }

  resetOfferManufacturerAdditionalDiscount(offer: Offer) {
    const priceDescriptor: PriceDescriptor = {
      lineId: DEALER_OFFER_PRICING_MAP.manufacturerAdditionalDiscount,
      stepId: DEALER_OFFER_PRICING_MAP.manufacturerAdditionalDiscount,
      value: 0
    };

    return this.removeOfferPricing(offer, priceDescriptor);
  }

  resetOfferManufacturerPositionDiscount(offer: Offer) {
    const priceDescriptor: PriceDescriptor = {
      lineId: DEALER_OFFER_PRICING_MAP.manufacturerPositionDiscount,
      stepId: DEALER_OFFER_PRICING_MAP.manufacturerPositionDiscount,
      value: 0
    };

    return this.removeOfferPricing(offer, priceDescriptor);
  }

  updateOfferPricing(
    offer: Pick<Offer, 'documentId' | 'revision'>,
    rate: number,
    stepId: string,
    key?: string,
    ignorePricingDisable?: boolean
  ): Observable<Quote> {
    const documentVersion: DocumentVersionInput = {
      documentId: offer.documentId,
      revision: offer.revision
    };
    let withPricing = true;

    return this.storeService.getOfferDisablePriceCalculation()
      .pipe(
        take(1),
        concatMap((priceCalculationOff) => {
          if (priceCalculationOff && !ignorePricingDisable) {
            withPricing = false;
          }

          return this.saveOfferPricing(documentVersion, rate, stepId, withPricing).pipe(
            tap((quote: Quote) => {
              let offerPricingUpdate = quote.pricing;
              let positionPricingUpdate = this.calculatePricingUpdate(quote);

              if (!withPricing) {
                offerPricingUpdate = this.setLocalOfferPricingByStepId(offer as Offer, stepId, rate);
                if (key) {
                  positionPricingUpdate = this.setLocalPositionsPricingByKey(offer as Offer, key, rate, false);
                }
                quote.pricing = offerPricingUpdate;
              }

              if (positionPricingUpdate || offerPricingUpdate) {
                let offerUpdate = mergeOfferPricingState({} as Offer, quote as OfferBase);

                offerUpdate = {
                  ...offerUpdate,
                  positionPricingUpdate,
                  offerPricingUpdate,
                  updatePositionPricingForms: true
                }
                this.storeService.setOfferUpdate(offerUpdate);
              } else {
                this.setOfferPricingStateInStore(quote);
              }

              return quote;
            }),
            map((quote: Quote) => quote),
            take(1)
          )
        })
      );
  }

  saveOfferPricing(
    documentVersion: DocumentVersionInput,
    rate: number,
    stepId: string,
    withPricing = true
  ): Observable<Quote> {

    return this.updateOfferPricingMutation
      .mutate(
        {
          documentVersion,
          rate: Number(rate),
          stepId,
          withPricing
        },
        { fetchPolicy: 'no-cache' }
      )
      .pipe<Quote, Quote, Quote>(
        map((r) => r.data?.quotes?.modify?.setPricingCommand as Quote),
        map((quote: Quote) => quote),
        take(1)
      );
  }

  updatePricing(
    offer: Pick<Offer, 'documentId' | 'revision'>,
    positionId: string,
    price: PriceDescriptor,
    withPricing?: boolean
  ): Observable<Offer> {
    const documentVersion: DocumentVersionInput = {
      documentId: offer.documentId,
      revision: offer.revision
    };

    const pricingRequired = (withPricing === undefined ? false : withPricing);

    return this.savePricing(
      documentVersion,
      positionId,
      price,
      pricingRequired
    ).pipe(
      map((quote: Quote) => {
        const offerPricingUpdate = quote.pricing;
        const positionPricingUpdate = (withPricing
          ? this.calculatePricingUpdate(quote)
          : this.setLocalPositionPricingByStepId(offer as Offer, positionId, price)
        );
        if (positionPricingUpdate) {
          this.storeService.setOfferUpdate({
            positionPricingUpdate,
            offerPricingUpdate,
            status: quote.status,
            snkModifier: this.offerMapperService.mapSnkModifier(quote)
          });
        }

        this.setOfferPricingStateInStore(quote);

        return { ...quote, positionPricingUpdate } as unknown as Offer;
      }),
      take(1)
    );
  }

  savePricing(
    documentVersion: DocumentVersionInput,
    positionId: string,
    price: PriceDescriptor,
    withPricing = true
  ): Observable<Quote> {

    return this.updatePricingMutation.mutate({
      documentVersion,
      lineId: positionId,
      priceLineId: price.lineId,
      stepId: price.stepId,
      rate: Number(price.value),
      withPricing
    }, { fetchPolicy: 'no-cache' })
      .pipe<Quote, Quote, Quote>(
        map((r) => r.data?.quotes?.modify?.line?.setPricingCommand as Quote),
        map((quote: Quote) => quote),
        take(1)
      );
  }

  removeOfferPricing(
    offer: Pick<Offer, 'documentId' | 'revision'>,
    price: PriceDescriptor,
    withPricing = true
  ): Observable<Offer> {
    const documentVersion: DocumentVersionInput = {
      documentId: offer.documentId,
      revision: offer.revision
    };

    return this.removeOfferPricingMutation
      .mutate(
        {
          documentVersion,
          priceLineId: price.lineId,
          stepId: price.stepId,
          withPricing
        },
        { fetchPolicy: 'no-cache' }
      )
      .pipe<Quote, Offer, Offer>(
        map((r) => r.data?.quotes?.modify?.removePricingCommand as Quote),
        map((quote: Quote) => ({ ...quote }) as unknown as Offer),
        take(1)
      );
  }

  resetOfferPricing(
    offer: Pick<Offer, 'documentId' | 'revision'>,
    price: PriceDescriptor,
    withPricing = true
  ): Observable<Offer> {
    const documentVersion: DocumentVersionInput = {
      documentId: offer.documentId,
      revision: offer.revision
    };

    return this.updateOfferPricingMutation
      .mutate(
        {
          documentVersion,
          stepId: price.stepId,
          rate: price.value || 0,
          withPricing
        },
        { fetchPolicy: 'no-cache' }
      )
      .pipe<Quote, Offer, Offer>(
        map((r) => r.data?.quotes?.modify?.setPricingCommand as Quote),
        map((quote: Quote) => ({ ...quote }) as unknown as Offer),
        take(1)
      );
  }

  removePositionPricing(
    documentVersion: DocumentVersionInput,
    positionId: string,
    price: PriceDescriptor,
    withPricing = true
  ): Observable<Offer> {

    return this.removePositionPricingMutation
      .mutate(
        {
          documentVersion,
          lineId: positionId,
          priceLineId: price.lineId,
          stepId: price.stepId,
          withPricing
        },
        { fetchPolicy: 'no-cache' }
      )
      .pipe<Quote, Offer, Offer>(
        map((r) => r.data?.quotes?.modify?.line?.removePricingCommand as Quote),
        map((quote: Quote) => ({ ...quote }) as unknown as Offer),
        take(1)
      );
  }

  resetOfferCalculations(
    offer: Pick<Offer, 'documentId' | 'revision'>,
    type: ConditionType,
    withPricing = true
  ): Observable<Offer[]> {
    const key = type === ConditionTypeENUM.SURCHARGE ? 'buyPriceSurcharge' : 'listPriceDiscount';
    const documentVersion: DocumentVersionInput = { documentId: offer.documentId, revision: offer.revision };

    const resetPrice: PriceDescriptor = {
      lineId: DEALER_OFFER_PRICING_MAP[key],
      stepId: DEALER_OFFER_PRICING_MAP[key],
      value: 0
    };

    return this.resetOfferPricing(offer, resetPrice, false).pipe(
      concatMap(() =>
        this.removeOfferPricing(offer, resetPrice, withPricing).pipe(
          concatMap((offerUpdate) => {
            let obs: Observable<Offer>[] = [];
            let lines: { id: string, pricing: ProductPricing | CustomPricing }[] = [];

            if (!withPricing) {
              const currentPricing = this.extractPricingUpdate(offer as Offer);
              if (currentPricing) {
                Object.entries(currentPricing).forEach(([id, pricing]) => lines.push({ id, pricing }));
              }
            } else {
              if (offerUpdate && offerUpdate.lines) {
                lines = offerUpdate.lines.filter(line => line.pricing).map((line) => ({
                  id: line.id, pricing: line.pricing as ProductPricing
                }));
              }
            }

            if (lines.length) {
              obs = lines.flatMap((pos) => {
                if ('pricing' in pos && pos.pricing) {
                  if ('elements' in pos.pricing) {
                    return pos.pricing.elements.flatMap((p) => (
                      (key in p && p[key]?.isChangedManually)
                        // reset product pricing if it's changed manually
                        ? this.removePositionPricing(documentVersion, pos.id, p[key] as PriceDescriptor, withPricing)
                        : []
                    ));
                  }
                  if (key in pos.pricing && pos.pricing[key].isChangedManually) {
                    // reset article pricing if it's changed manually
                    return this.removePositionPricing(documentVersion, pos.id, pos.pricing[key], withPricing);
                  }
                }

                return [];
              });
            }

            return concat(...obs).pipe(toArray(), take(1));
          })
        )
      )
    );
  }

  ensurePricesUpdated(
    offer: Pick<Offer, 'documentId' | 'revision'>,
    positionId?: string,
    withPricing = true
  ): Observable<Offer> {
    const documentVersion: DocumentVersionInput = {
      documentId: offer.documentId,
      revision: offer.revision
    };

    return this.ensurePricesUpdatedMutation
      .mutate({
      documentVersion,
      lineId: positionId,
      withPricing
    }, { fetchPolicy: 'no-cache' })
      .pipe<Quote, Offer, Offer>(
        map((r) => r.data?.quotes?.ensurePricesUpdated as Quote),
        map((quote: Quote) => quote as unknown as Offer),
        take(1)
      );
  }

  ensureOfferPricesUpdated(
    offer: Pick<Offer, 'documentId' | 'revision'>,
    withPricing = true
  ): Observable<Offer> {
    const documentVersion: DocumentVersionInput = {
      documentId: offer.documentId,
      revision: offer.revision
    };

    return this.ensureOfferPricesUpdatedMutation
      .mutate({ documentVersion, withPricing }, { fetchPolicy: 'no-cache' })
      .pipe<Quote, Quote, Offer, Offer>(
        map((r) => r.data?.quotes?.ensurePricesUpdated as Quote),
        tap((quote: Quote) => {
          if (withPricing) {
            const positionPricingUpdate = this.calculatePricingUpdate(quote as Quote);
            if (positionPricingUpdate) {
              this.storeService.setOfferUpdate({
                positionPricingUpdate,
                offerPricingUpdate: quote.pricing,
                updatePositionPricingForms: true,
                autoUpdatePrices: quote.autoUpdatePrices,
                priceIsOutdated: quote.priceIsOutdated
              });
            } else {
              this.setOfferPricingStateInStore(quote);
            }
          } else {
            this.setOfferPricingStateInStore(quote);
          }
        }),
        map((quote: Quote) => {
          let o = quote as unknown as Offer;

          if(!o.revision) {
            o = { ...o, revision: offer.revision }
          }

          return o;
        }),
        take(1)
      );
  }

  setAutoUpdatePrices(
    offer: Pick<Offer, 'documentId' | 'revision'>,
    autoUpdatePrices: boolean,
    updateStateInStore = true
  ): Observable<Offer> {
    const documentVersion: DocumentVersionInput = {
      documentId: offer.documentId,
      revision: offer.revision
    };

    return this.setAutoUpdatePricesMutation.mutate({ documentVersion, autoUpdatePrices }, { fetchPolicy: 'no-cache' })
      .pipe<Quote, Quote, Offer, Offer>(
        map((r) => r.data?.quotes?.modify?.setAutoUpdatePrices as Quote),
        tap((quote: Quote) => {
          if (updateStateInStore) {
            this.setOfferPricingStateInStore(quote)
          }
        }),
        map((quote: Quote) => quote as unknown as Offer),
        take(1)
      );
  }

  setCurrentOfferAutoUpdatePrices(autoUpdatePrices: boolean, calculatePrices = true): Observable<Offer> {
    return this.storeService.getCurrentOffer()
      .pipe(
        take(1),
        concatMap((offer) => {
          if (offer?.documentId) {
            let obs$ = of(offer);

            if (offer?.priceIsOutdated && calculatePrices) {
              obs$ = this.ensureOfferPricesUpdated(offer, false);
            }

            return obs$.pipe(
              concatMap((_) => {
                return this.setAutoUpdatePrices(offer, autoUpdatePrices);
              })
            );
          }

        return of({} as Offer);
      })
    );
  }

  setOfferAutoUpdatePrices(offer: Offer, autoUpdatePrices: boolean): Observable<Offer> {
    let obs$ = of(offer);

    return obs$.pipe(
      concatMap((offer) => {
        if (offer?.priceIsOutdated && autoUpdatePrices) {
          return this.ensureOfferPricesUpdated(offer, true);
        }

        return of(offer);
      }),
      concatMap((offer) => {
        if (offer?.autoUpdatePrices === false) {
          return this.setAutoUpdatePrices(offer, autoUpdatePrices);
        }

        return of(offer);

      }),
      map((offer) => offer),
      take(1)
    );
  }

  turnOffOfferPricing(
    offer: Pick<Offer, 'documentId' | 'revision'>,
    updateStateInStore = true
  ): Observable<Offer> {
    return this.setAutoUpdatePrices(offer, false, updateStateInStore);
  }

  turnOnOfferPricing(
    offer: Pick<Offer, 'documentId' | 'revision'>,
    updateStateInStore = true
  ): Observable<Offer> {
    return this.setAutoUpdatePrices(offer, true, updateStateInStore);
  }

  turnOffCurrentOfferPricing(): Observable<Offer> {
    return this.setCurrentOfferAutoUpdatePrices(false);
  }

  turnOnCurrentOfferPricing(): Observable<Offer> {
    return this.setCurrentOfferAutoUpdatePrices(true);
  }

  /** discount as in percent 0 - 100 (or outside, just not 0 - 1) */
  private applyDiscount(discount: number, value: number): number {
    return discount === 0 ? value : value * (1 - discount / 100);
  }

  private applySurcharge(discount: number, value: number): number {
    return discount === 0 ? value : value * (1 + discount / 100);
  }

  calculateManufacturerTotalSellPrice({
    listPrice,
    baseDiscount,
    additionalDiscount,
    alternativeDiscount,
    positionDiscount,
    tz,
    quantity
  }: {
    listPrice: number;
    baseDiscount: number;
    additionalDiscount: number;
    alternativeDiscount: number;
    positionDiscount: number;
    tz: number;
    quantity: number;
  }): number {
    let priceWithPosDiscount;

    if (alternativeDiscount === 0) {
      const priceWithBaseDiscount = this.applyDiscount(baseDiscount, listPrice);
      const priceWithAddDiscount = this.applyDiscount(additionalDiscount, priceWithBaseDiscount);
      priceWithPosDiscount = this.applyDiscount(positionDiscount, priceWithAddDiscount);
    } else {
      const priceWithAltDiscount = this.applyDiscount(alternativeDiscount, listPrice);
      priceWithPosDiscount = this.applyDiscount(positionDiscount, priceWithAltDiscount);
    }

    return this.applySurcharge(tz, priceWithPosDiscount) * quantity;
  }

  calculateManufacturerAlternativeDiscount({
    totalSellPrice,
    listPrice,
    positionDiscount,
    tz,
    quantity
  }: {
    totalSellPrice: number;
    listPrice: number;
    positionDiscount: number;
    tz: number;
    quantity: number;
  }): number {
    const priceWithDiscount = this.applySurcharge(positionDiscount, listPrice);
    const priceWithDiscountAndSurcharge = this.applySurcharge(tz, priceWithDiscount);

    return (1 - totalSellPrice / (priceWithDiscountAndSurcharge * quantity)) * -100; // in quote "-" means discount
  }

  removePricingCommands(
    offer: Pick<Offer, 'documentId' | 'revision'>,
    excludeStepIds?: string[]
  ): Observable<Quote> {
    const documentId: DocumentVersionInput = {
      documentId: offer.documentId,
      revision: offer.revision
    };

    return this.removePricingCommandsMutation
      .mutate(
        {
          documentId,
          excludeStepIds
        },
        { fetchPolicy: 'no-cache' }
      )
      .pipe<Quote, Quote>(
        map((r) => r.data?.custom?.removePricingCommands as Quote),
        take(1)
      );
  }

  hasOfferManufacturerDiscounts(offer: Offer | OfferBase): boolean {
    const currentOffer = offer as Offer;

    return currentOffer.offerPricingUpdate
      ? !!currentOffer.offerPricingUpdate?.buyDiscount?.value ||
      Boolean(currentOffer.offerPricingUpdate?.manufacturerAlternativeDiscount?.isChangedManually) ||
      Boolean(currentOffer.offerPricingUpdate?.manufacturerPositionDiscount?.isChangedManually) ||
      Boolean(currentOffer.offerPricingUpdate?.manufacturerAdditionalDiscount?.isChangedManually)
      : !!currentOffer.pricing?.buyDiscount?.value ||
      Boolean(currentOffer.pricing?.manufacturerAlternativeDiscount?.isChangedManually) ||
      Boolean(currentOffer.pricing?.manufacturerPositionDiscount?.isChangedManually) ||
      Boolean(currentOffer.pricing?.manufacturerAdditionalDiscount?.isChangedManually);
  }

  hasPositionManufacturerDiscounts(pricing: ElementPricing[]): boolean {
    return pricing
      ? pricing.some((pr) => {
        return (
          pr.alternativeDiscount?.isChangedManually ||
          pr.positionDiscount?.isChangedManually ||
          pr.additionalDiscount?.isChangedManually
        );
      })
      : false;
  }

  getResetDiscountsObservables(
    offer: Offer,
    hasOfferDiscount: boolean,
    hasPositionDiscounts: boolean,
    line?: ProductLine | CustomLine
  ): Observable<any>[] {
    const observables: Observable<any>[] = [];

    if (hasOfferDiscount) {
      observables.push(...this.getResetOfferDiscountsObservables(offer));
    }

    if (line && hasPositionDiscounts) {
      const lineObservables = this.getResetPositionDiscountsObservables(
        { documentId: offer.documentId, revision: offer.revision },
        line
      );
      if (lineObservables.length) {
        observables.push(...lineObservables);
      }
    }

    return observables;
  }

  getResetOfferDiscountsObservables(
    offer: Offer
  ): Observable<any>[] {
    return [
      this.resetOfferManufacturerDiscount(offer),
      this.resetOfferManufacturerAlternativeDiscount(offer),
      this.resetOfferManufacturerAdditionalDiscount(offer),
      this.resetOfferManufacturerPositionDiscount(offer)
    ];
  }

  getResetPositionDiscountsObservables(
    documentVersion: DocumentVersionInput,
    line: ProductLine | CustomLine
  ): Observable<any>[] {
    const observables: Observable<any>[] = [];
    const elements = (line!.pricing as ProductPricing).elements;

    if (elements) {
      elements.forEach((element) => {
        if (element.positionDiscount && element.positionDiscount?.isChangedManually) {
          observables.push(
            this.removePositionPricing(documentVersion, line.id, element.positionDiscount as PriceDescriptor)
          );
        }

        if (element.alternativeDiscount && element.alternativeDiscount?.isChangedManually) {
          observables.push(
            this.removePositionPricing(documentVersion, line.id, element.alternativeDiscount as PriceDescriptor)
          );
        }

        if (element.additionalDiscount && element.additionalDiscount?.isChangedManually) {
          observables.push(
            this.removePositionPricing(documentVersion, line.id, element.additionalDiscount as PriceDescriptor)
          );
        }
      });
    }

    return observables;
  }

  /* Update price value for all offer positions by pricing field name */
  setLocalPositionsPricingByKey(
    offer: Offer,
    key: string,
    value: number | string,
    resetChangedManually?: boolean
  ): PositionPricingUpdate | undefined {
    if (!offer || !offer.productPositions) {
      return undefined;
    }

    const pricingUpdate = this.extractPricingUpdate(offer);

    if (pricingUpdate) {
      Object.entries(pricingUpdate).forEach(([id, pricing]) => {
        const updatedPricing = {
          ...pricing,
          ...setPricingObjectValueChanged(pricing, key, value, resetChangedManually)
        }

        pricingUpdate[id] = updatedPricing;

        if (updatedPricing.elements) {
          pricingUpdate[id] = {
            ...updatedPricing,
            elements: updateLineElementsPricingByKey(updatedPricing.elements, key, value, resetChangedManually)
          };
        }
      });
    }

    return pricingUpdate;
  }

  /* Update price value for single offer position by pricing stepId */
  setLocalPositionPricingByStepId(
    offer: Offer,
    positionId: string,
    price: PriceDescriptor
  ): PositionPricingUpdate | undefined {
    if (!offer || !offer.productPositions) {
      return undefined;
    }

    const pricingUpdate = this.extractPricingUpdate(offer);

    if (pricingUpdate && pricingUpdate[positionId]) {
      let linePricing = pricingUpdate[positionId] as ProductPricing;

      pricingUpdate[positionId] = {
        ...linePricing,
        ...setPriceRateByStepId(linePricing, price.stepId, price.value, (!linePricing.elements ? true : undefined))
      }

      if (linePricing.elements) {
        pricingUpdate[positionId] = {
          ...pricingUpdate[positionId],
          elements: mergeLineElementsPricing(linePricing.elements, price.stepId, price.value)
        };
      }
    }

    return pricingUpdate;
  }

  /* Update offer price value by stepId locally */
  setLocalOfferPricingByStepId(offer: Offer, stepId: string, rate: number) {
    if (!offer.offerPricingUpdate) {
      return undefined;
    }

    const offerPricingUpdate = setPriceRateByStepId(offer.offerPricingUpdate, stepId, rate);

    return offerPricingUpdate as OfferPricing;
  }

  /* Update offer price value by field name locally */
  setLocalOfferPricingByKey(offer: Offer, key: string, rate: number) {
    if (!offer.offerPricingUpdate) {
      return undefined;
    }

    return {
      ...offer.offerPricingUpdate,
      ...setPricingObjectValue(offer.offerPricingUpdate, key, rate)
    } as OfferPricing;
  }

  /* Update offer price state in the local storage */
  setOfferPricingStateInStore(data: OfferBase | Quote | Partial<Offer>): void {
    let pricingState = {};

    if (data.autoUpdatePrices !== undefined) {
      pricingState = { ...pricingState, autoUpdatePrices: data.autoUpdatePrices };
    }

    if (data.priceIsOutdated !== undefined) {
      pricingState = { ...pricingState, priceIsOutdated: data.priceIsOutdated };
    }

    if (Object.keys(pricingState).length) {
      this.storeService.setOfferUpdate(pricingState as Partial<Offer>);
    }
  }

}
